/**
 * Cartouche (nom de la station, date, altitudes, chiffres du domaine) et
 * légende du plan des pistes.
 */
import { DIFFICULTY_LABEL, type Difficulty } from '@snowchain/shared';
import { PICTO_LABEL, type PictoKind } from './overlay';
import { km, meters, PictoGlyph, PISTE_COLOR, WORKS_COLOR } from './style';

export interface CartoucheData {
  station: string;
  date: string;
  village: number;
  summit: number;
  openKm: number;
  totalKm: number;
  byColor: Record<Difficulty, number>;
  liftsOpen: number;
  liftsTotal: number;
  pistesOpen: number;
  pistesTotal: number;
}

const COLORS: Difficulty[] = ['verte', 'bleue', 'rouge', 'noire'];

export function Cartouche({ c }: { c: CartoucheData }) {
  return (
    <div class="tm-cartouche">
      <div class="tm-c-station">{c.station}</div>
      <div class="tm-c-title">Plan des pistes</div>
      <div class="tm-c-rule" />
      <div class="tm-c-date">{c.date}</div>
      <div class="tm-c-alt">
        <span>Village {meters(c.village)}</span>
        <span class="tm-dot">·</span>
        <span>Sommet {meters(c.summit)}</span>
      </div>
      <div class="tm-c-km">
        <strong>{km(c.openKm * 1000)}</strong> ouverts sur {km(c.totalKm * 1000)}
      </div>
      <div class="tm-c-counts">
        {COLORS.map((d) => (
          <span key={d} class="tm-c-count" title={`Pistes ${DIFFICULTY_LABEL[d].toLowerCase()}s`}>
            <i style={{ background: PISTE_COLOR[d] }} />
            {c.byColor[d]}
          </span>
        ))}
      </div>
      <div class="tm-c-lifts">
        Pistes ouvertes {c.pistesOpen} / {c.pistesTotal}
        <span class="tm-dot">·</span>
        Remontées {c.liftsOpen} / {c.liftsTotal}
      </div>
    </div>
  );
}

function Sample({ color, dash, faded }: { color: string; dash?: string; faded?: boolean }) {
  return (
    <svg width="34" height="12" viewBox="0 0 34 12" aria-hidden="true">
      <path d="M2 6 L32 6" stroke="#fff" stroke-width="6" stroke-linecap="round" />
      <path d="M2 6 L32 6" stroke={color} stroke-width="3.2" stroke-linecap="round" stroke-dasharray={dash} opacity={faded ? 0.5 : 1} />
    </svg>
  );
}

function LiftSample({ kind }: { kind: 'teleski' | 'telesiege' | 'telecabine' }) {
  return (
    <svg width="34" height="12" viewBox="0 0 34 12" aria-hidden="true">
      <path d="M3 6 L31 6" stroke="#111" stroke-width={kind === 'teleski' ? 1.3 : 1.8} />
      {kind === 'telesiege' && <path d="M11 3 L11 9 M17 3 L17 9 M23 3 L23 9" stroke="#111" stroke-width="1.3" />}
      {kind === 'telecabine' && <path d="M10 7 h4.4 v3.6 h-4.4z M19.6 7 h4.4 v3.6 h-4.4z" fill="#111" />}
      <rect x="0.5" y="3" width="6" height="6" fill="#111" />
      <rect x="27.5" y="3" width="6" height="6" fill="#111" />
    </svg>
  );
}

const LEGEND_PICTOS: PictoKind[] = ['caisses', 'restaurant', 'snack', 'secours', 'ecole', 'location', 'hebergement', 'parking', 'garage'];

export function Legend() {
  return (
    <div class="tm-legend">
      <div class="tm-l-title">Légende</div>
      <div class="tm-l-grid">
        {COLORS.map((d) => (
          <div key={d} class="tm-l-row">
            <Sample color={PISTE_COLOR[d]} />
            <span>Piste {DIFFICULTY_LABEL[d].toLowerCase()}</span>
          </div>
        ))}
        <div class="tm-l-row">
          <Sample color={PISTE_COLOR.bleue} dash="5 4" faded />
          <span>Piste fermée</span>
        </div>
        <div class="tm-l-row">
          <Sample color={WORKS_COLOR} dash="2 4" />
          <span>Piste en travaux</span>
        </div>
        <div class="tm-l-row">
          <LiftSample kind="teleski" />
          <span>Téléski</span>
        </div>
        <div class="tm-l-row">
          <LiftSample kind="telesiege" />
          <span>Télésiège</span>
        </div>
        <div class="tm-l-row">
          <LiftSample kind="telecabine" />
          <span>Télécabine</span>
        </div>
        <div class="tm-l-row">
          <svg width="34" height="12" viewBox="0 0 34 12" aria-hidden="true">
            <path d="M3 6 L31 6" stroke="#111" stroke-width="1.8" stroke-dasharray="4 3" opacity="0.5" />
          </svg>
          <span>Remontée à l'arrêt</span>
        </div>
      </div>
      <div class="tm-l-pictos">
        {LEGEND_PICTOS.map((k) => (
          <div key={k} class="tm-l-picto">
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <PictoGlyph kind={k} x={8} y={8} size={14} />
            </svg>
            <span>{PICTO_LABEL[k]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
